/**
 * Разбор кодов маркировки DataMatrix (GS1) на кассе.
 *
 * Код маркировки — это GS1-строка вида
 *   `01<GTIN-14>21<серийный номер>[GS 91<...>][GS 92<...>][GS 93<...>]`
 * где GS — разделитель FNC1 (ASCII 29, `\u001d`).
 *
 * Почему локальная проверка обязательна (ARCH-TASK-65.12.1):
 *  - True API нельзя вызывать из цикла сканирования на ККТ
 *    (лимит 2 с, документация прямо запрещает);
 *  - значит структуру кода (AI, длины, GS, контрольный разряд GTIN)
 *    проверяем локально, а статус в обороте — через ККТ/ЕСМ (65.12.2+);
 *  - регистр серийного номера значим: приведение к верхнему/нижнему
 *    регистру ломает код, поэтому никаких `toUpperCase()`.
 */

/** Разделитель GS1 FNC1 (ASCII 29). Часть кода, а не мусор. */
export const GS = '\u001d'

/** Допустимые символы серийного номера (GS1 CSET 82). */
const SERIAL_RE = /^[A-Za-z0-9!#%&'()*+,\-./:;<=>?_]+$/

/** Допустимые символы криптохвоста (base64-подобные). */
const CRYPTO_RE = /^[A-Za-z0-9+/=_-]+$/

/** Максимальная длина серийного номера по GS1. */
const SERIAL_MAX = 20

/** Статус разбора кода маркировки. */
export type MarkingStatus =
  | 'ok'
  | 'empty'
  | 'tooShort'
  | 'noAi01'
  | 'badGtin'
  | 'badSerial'
  | 'noGs'
  | 'truncated'
  | 'badCrypto'

/** Результат разбора кода маркировки. */
export interface ParsedMarkingCode {
  /** Код как пришёл от сканера (только trim/очистка переводов строк). */
  raw: string
  /** Код для передачи в ККТ/API: GS сохранён, регистр не изменён. */
  normalized: string
  status: MarkingStatus
  isValid: boolean
  /** GTIN-14 (как в AI 01). */
  gtin14?: string
  /** GTIN-13 для сопоставления с каталогом (ведущий ноль убран). */
  gtin13?: string
  /** Серийный номер (регистр сохранён). */
  serial?: string
  crypto91?: string
  crypto92?: string
  ai93?: string
  /** Есть ли в коде разделитель GS. */
  hasGs: boolean
  /** Диагностика: серийник содержит строчные буквы (сканер мог изменить регистр). */
  hasLowercaseSerial: boolean
  /** Человекочитаемые причины (для UI). */
  reasons: string[]
}

/**
 * Очистить ввод сканера: убрать переводы строк/табуляцию,
 * НЕ трогая разделитель GS и регистр символов.
 */
function sanitizeMarkingInput(raw: string): string {
  return String(raw ?? '')
    .replace(/[\r\n\t]+/g, '')
    .trim()
}

/** Проверка контрольного разряда GS1 — единая реализация с розничными ШК. */
import { isValidEanChecksum as isValidGs1Checksum } from '@core/utils/barcodeValidator'

/** GTIN-14 → GTIN-13 (для каталога): убираем ведущий ноль. */
function toGtin13(gtin14: string): string {
  return gtin14.length === 14 && gtin14.startsWith('0') ? gtin14.slice(1) : gtin14
}

/**
 * Признак потерянного разделителя GS: в конце строки стоит криптохвост
 * (AI 93 + 4 символа либо 91/92 + 4..44 символа), а перед ним — непустой
 * серийник. Длина серийника переменная, поэтому AI ищем по суффиксу.
 */
function findLostGsTail(rest: string): { index: number; ai: string } | null {
  const candidates: Array<{ index: number; ai: string }> = []
  const m93 = /93[A-Za-z0-9]{4}$/.exec(rest)
  if (m93 && m93.index >= 1) candidates.push({ index: m93.index, ai: '93' })
  const m9x = /9[12][A-Za-z0-9+/=_-]{4,44}$/.exec(rest)
  if (m9x && m9x.index >= 1) candidates.push({ index: m9x.index, ai: m9x[0].slice(0, 2) })
  if (candidates.length === 0) return null
  return candidates.sort((a, b) => a.index - b.index)[0]
}

interface BaseParse {
  raw: string
  normalized: string
  hasGs: boolean
}

function fail(
  base: BaseParse,
  status: MarkingStatus,
  reason: string,
  extra: Partial<ParsedMarkingCode> = {}
): ParsedMarkingCode {
  // Дополняем частично разобранными полями, но статус/валидность/причина
  // всегда берём из аргументов — иначе `...extra` перезапишет отказ.
  return {
    ...extra,
    raw: base.raw,
    normalized: base.normalized,
    status,
    isValid: false,
    hasGs: base.hasGs,
    hasLowercaseSerial: extra.hasLowercaseSerial ?? false,
    reasons: [reason]
  }
}

/**
 * Разобрать код маркировки: AI 01 (GTIN), AI 21 (серийник),
 * криптохвост 91/92/93 после разделителя GS.
 */
export function parseMarkingCode(raw: string): ParsedMarkingCode {
  const normalized = sanitizeMarkingInput(raw)
  const base: BaseParse = { raw, normalized, hasGs: normalized.includes(GS) }

  if (!normalized) {
    return fail(base, 'empty', 'Код маркировки пустой')
  }
  // Сначала отличаем «не маркировку» от «обрезанного кода маркировки»:
  // для UI это разные сообщения (EAN-13 не должен жаловаться на обрезку).
  if (!normalized.startsWith('01')) {
    return fail(base, 'noAi01', 'Это не код маркировки (нет идентификатора AI 01)')
  }
  if (normalized.length < 16) {
    return fail(base, 'tooShort', 'Код маркировки слишком короткий — просканируйте заново')
  }

  const gtin14 = normalized.slice(2, 16)
  if (!/^\d{14}$/.test(gtin14)) {
    return fail(base, 'truncated', 'GTIN в коде маркировки обрезан')
  }
  if (!isValidGs1Checksum(gtin14)) {
    return fail(base, 'badGtin', 'Неверный контрольный разряд GTIN', { gtin14 })
  }

  let rest = normalized.slice(16)
  if (!rest.startsWith('21')) {
    return rest.length === 0
      ? fail(base, 'truncated', 'Код маркировки обрезан: нет серийного номера', { gtin14 })
      : fail(base, 'badSerial', 'Нет идентификатора серийного номера (AI 21)', { gtin14 })
  }
  rest = rest.slice(2)

  // После серийника должен идти GS (если есть продолжение кода).
  const gsIdx = rest.indexOf(GS)
  let serial: string
  let tail: string
  if (gsIdx >= 0) {
    serial = rest.slice(0, gsIdx)
    tail = rest.slice(gsIdx + 1)
  } else {
    // Без GS: либо код заканчивается серийником, либо разделитель потерян.
    // Длина серийника переменная (1..20), поэтому «съесть» AI молча —
    // худший исход: код уйдёт в ККТ с неверным серийником и ЧЗ его не найдёт.
    const lost = findLostGsTail(rest)
    if (lost) {
      return fail(base, 'noGs', 'В коде нет разделителя GS — проверьте настройки сканера', {
        gtin14,
        gtin13: toGtin13(gtin14),
        serial: rest.slice(0, lost.index)
      })
    }
    serial = rest
    tail = ''
  }

  if (!serial || serial.length > SERIAL_MAX || !SERIAL_RE.test(serial)) {
    return fail(base, 'badSerial', 'Некорректный серийный номер в коде маркировки', {
      gtin14,
      gtin13: toGtin13(gtin14),
      hasLowercaseSerial: /[a-z]/.test(serial ?? '')
    })
  }

  const result: ParsedMarkingCode = {
    raw,
    normalized,
    status: 'ok',
    isValid: true,
    gtin14,
    gtin13: toGtin13(gtin14),
    serial,
    hasGs: base.hasGs,
    hasLowercaseSerial: /[a-z]/.test(serial),
    reasons: []
  }

  if (!tail) return result
  return parseCryptoTail(base, tail, result)
}

/** Разбор криптохвоста (AI 91/92/93), идущего после серийного номера. */
function parseCryptoTail(
  base: BaseParse,
  tail: string,
  result: ParsedMarkingCode
): ParsedMarkingCode {
  let remaining = tail
  while (remaining.length > 0) {
    const ai = remaining.slice(0, 2)
    if (!/^\d{2}$/.test(ai)) {
      return fail(base, 'badCrypto', 'Не удалось разобрать криптохвост кода маркировки', result)
    }
    const nextGs = remaining.indexOf(GS, 2)
    const value = nextGs >= 0 ? remaining.slice(2, nextGs) : remaining.slice(2)
    remaining = nextGs >= 0 ? remaining.slice(nextGs + 1) : ''

    if (!value || !CRYPTO_RE.test(value)) {
      return fail(base, 'badCrypto', `Некорректное значение криптохвоста (AI ${ai})`, result)
    }
    if (ai === '91') {
      result.crypto91 = value
    } else if (ai === '92') {
      result.crypto92 = value
    } else if (ai === '93') {
      if (value.length !== 4) {
        return fail(base, 'badCrypto', 'Код проверки (AI 93) должен быть 4 символа', result)
      }
      result.ai93 = value
    } else {
      return fail(base, 'badCrypto', `Неизвестный идентификатор в коде маркировки (AI ${ai})`, result)
    }
  }
  return result
}

/** Нормализованный код для передачи в ККТ (тег 2000) и в API. */
export function normalizeMarkingCode(raw: string): string {
  return sanitizeMarkingInput(raw)
}

/**
 * Похож ли отсканированный код на DataMatrix «Честный Знак».
 *
 * Признак — идентификатор AI 01 в начале (допускаем обрезку до 12 цифр GTIN).
 * Наличие одного разделителя GS признаком НЕ считаем: иначе любой текст с
 * символом 29 уводил бы обычный скан в модалку марок.
 */
export function looksLikeDataMatrix(code: string): boolean {
  const clean = sanitizeMarkingInput(code)
  return /^01\d{12,14}/.test(clean)
}

/**
 * Извлечь GTIN/EAN из кода маркировки (AI 01) либо из «голого» GTIN-14/EAN-13.
 *
 * @returns 13-значный GTIN для сопоставления с каталогом или null.
 */
export function extractGtinFromDataMatrix(raw: string): string | null {
  const parsed = parseMarkingCode(raw)
  if (parsed.isValid && parsed.gtin13) return parsed.gtin13

  // Fallback: «голый» GTIN-14 / EAN-13 без AI (старые коды, ручной ввод).
  const clean = sanitizeMarkingInput(raw)
  if (/^\d{13,14}$/.test(clean) && isValidGs1Checksum(clean)) {
    return toGtin13(clean)
  }
  return null
}

/** Значение маркировки для ФФД 1.2 (тег 2000): GS и регистр сохраняются. */
export function markingValueForFfd12(parsed: ParsedMarkingCode): string {
  return parsed.normalized
}

/** Годен ли код для продажи с точки зрения структуры. */
export function isMarkingAcceptable(parsed: ParsedMarkingCode): boolean {
  return parsed.isValid
}

/** Человекочитаемое описание проблемы кода (для UI и причин отказа). */
export function markingStatusText(parsed: ParsedMarkingCode): string {
  if (parsed.isValid) return 'Код маркировки корректен'
  return parsed.reasons[0] ?? 'Некорректный код маркировки'
}
